<template>
  <VAlert class="dialog-alert" :class="getClass" :data-qa="dataQa" :width="width" :icon="getIcon">
    <VcLayout
      align-start
      column
      class="alert-content"
      :class="{ 'dialog-alert__close-btn': closeButton }">
      <span v-if="headerText && !isInlineHeader" class="alert-header">
        {{ headerText }}
      </span>
      <span>
        <span class="alert-body" :class="{ 'alert-body--with-inline-action': inlineActionText }">
          <slot name="body" :bodyText="bodyText">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-if="bodyText" v-html="getBodyText"></span>
          </slot>
        </span>
        <span v-if="inlineActionText" class="inline-action" @click="$emit('onInlineAction')">
          {{ inlineActionText }}
        </span>
      </span>
    </VcLayout>
    <VcButton
      v-if="buttonText"
      class="action-button"
      plain
      data-qa="alert-action-btn"
      @click="$emit('onAction')">
      {{ buttonText }}
      <template #append>
        <VcIcon>$arrow_right</VcIcon>
      </template>
    </VcButton>
    <VcButton
      v-for="(button, index) in decoratedButtonsData"
      v-bind="button"
      :key="index"
      class="multi-action-button"
      @click="$emit('action' + index, button)" />
    <VcButton
      v-if="closeButton"
      class="close-button"
      data-qa="alert-close-btn"
      fab
      plain
      @click="$emit('closeAlert')">
      <template #prepend>
        <VcIcon>$close_button</VcIcon>
      </template>
    </VcButton>
  </VAlert>
</template>

<script>
import VcLayout from '../VcLayout/VcLayout.vue';
import VcButton from '../VcButton/VcButton.vue';
import VcIcon from '../VcIcon/VcIcon.vue';

export default {
  name: 'VcAlert',
  components: { VcIcon, VcButton, VcLayout },
  props: {
    type: {
      type: String,
      required: true,
      validator: (prop) =>
        ['attention', 'warning', 'info', 'success', 'app', 'recommendation'].includes(prop),
    },
    headerText: {
      type: String,
    },
    bodyText: {
      type: String,
    },
    buttonText: {
      type: String,
    },
    /**
     * Array of buttons to display in the alert, each param represents the params that will be passed to a VcButton component.
     * Each button will emit an event based on its index in this array.
     * The first button will emit 'action0', the next will be 'action1', etc.
     * The event data will include the item data from the array.
     */
    buttonsData: {
      type: Array,
      default: () => [],
    },
    inlineActionText: {
      type: String,
    },
    isStripe: {
      type: Boolean,
      default: false,
    },
    isInline: {
      type: Boolean,
      default: false,
    },
    closeButton: {
      type: Boolean,
      default: false,
    },
    isInlineHeader: {
      type: Boolean,
      default: false,
    },
    width: {
      type: [String, Number],
    },
    dataQa: {
      type: String,
      default: 'vc-alert',
    },
  },
  computed: {
    getStripe() {
      return this.isStripe ? ' stripe' : '';
    },
    getInline() {
      return this.isInline ? ' inline' : '';
    },
    getClass() {
      return `vcita-alert-${this.type}${this.getStripe}${this.getInline}`;
    },
    getIcon() {
      if (this.isInline) {
        switch (this.type) {
          case 'warning':
            return '$warning_fill';
          case 'info':
            return '$info_fill';
          case 'success':
            return '$check_fill';
          case 'app':
            return '$app';
          case 'recommendation':
            return '$ai_sparkles';
          default:
            return '$attention_fill';
        }
      }

      switch (this.type) {
        case 'warning':
          return '$warning';
        case 'info':
          return '$info';
        case 'success':
          return '$check_circle';
        case 'app':
          return '$app';
        case 'recommendation':
          return '$ai_sparkles';
        default:
          return '$error';
      }
    },
    getBodyText() {
      if (this.isInlineHeader && this.headerText) {
        return `<span class="alert-header">${this.headerText}</span> ${this.bodyText}`;
      }
      return this.bodyText;
    },
    decoratedButtonsData() {
      return this.buttonsData.map((button) => {
        return {
          pill: true,
          flavor: 'ai',
          dataQa: button.dataQa || 'VcAlert-action-btn',
          ...button,
        };
      });
    },
  },
};
</script>

<style lang="scss">
@import '../../scss/_i18n-mixins.scss';
@import '../../scss/mixins.scss';

.dialog-alert {
  padding: var(--size-value3);
  margin: var(--size-value0);

  &__close-btn {
    margin-inline-end: var(--size-value7);
  }

  .alert-content {
    padding-inline-end: var(--size-value9);
  }

  .v-alert__icon {
    margin-inline-end: var(--size-value2);
    font-size: var(--size-value5);
    height: var(--size-value5);
    min-width: var(--size-value5);
  }

  .close-button {
    position: absolute;
    width: var(--size-value6);
    height: var(--size-value6);
    top: var(--size-value3);
    @include i-position-mirror(right, var(--size-value3));

    .v-btn__content {
      opacity: unset;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.07);
    }

    .v-icon {
      margin-right: var(--size-value0);

      svg {
        fill: var(--gray-darken-2);
        width: var(--size-value4);
        height: var(--size-value4);
      }
    }
  }

  &.stripe.v-alert {
    border-radius: var(--size-value0);
    border-top: none;
    border-left: none;
    border-right: none;
  }

  &.inline.v-alert {
    border-radius: var(--size-value0);
    border: none;
    padding: var(--size-value0);
    background-color: transparent;
  }

  &.vcita-alert-attention {
    border: 1px solid var(--red-lighten-2);
    background-color: var(--red-lighten-3);

    .v-icon {
      svg.vc-notif,
      svg.vc-attention-fill {
        fill: var(--red);
      }
    }
  }

  &.vcita-alert-warning {
    border: 1px solid var(--yellow-lighten-2);
    background-color: var(--yellow-lighten-3);

    .v-alert__wrapper .v-icon {
      svg.vc-notif,
      svg.vc-warning-fill {
        fill: var(--yellow);
      }
    }
  }

  &.vcita-alert-info {
    border: 1px solid var(--blue-lighten-2);
    background-color: var(--blue-lighten-3);

    .v-alert__wrapper .v-icon {
      svg.vc-notif,
      svg.vc-info-fill {
        fill: var(--blue);
      }
    }
  }

  &.vcita-alert-success {
    border: 1px solid var(--green-lighten-2);
    background-color: var(--green-lighten-3);

    .v-icon {
      svg.vc-notif,
      svg.vc-check-fill {
        fill: var(--green);
      }
    }
  }

  &.vcita-alert-app {
    border: 1px solid var(--purple-lighten-2);
    background-color: var(--purple-lighten-3);

    .v-icon {
      svg.vc-notif,
      svg.vc-check-fill {
        fill: var(--purple);
      }
    }

    .v-alert__wrapper {
      justify-content: center;

      .v-alert__content {
        flex: revert;
      }
    }

    .inline-action {
      color: var(--purple);
    }
  }

  &.vcita-alert-recommendation {
    background-image: var(--ai-background);

    .v-icon {
      svg.vc-notif,
      svg.vc-notif {
        fill: var(--ai-main);
      }
    }

    .v-alert__wrapper {
      .v-alert__content {
        flex: revert;
      }
    }

    .inline-action {
      color: var(--purple);
    }
  }

  .v-alert__wrapper .v-icon__component {
    height: var(--size-value4);
    width: var(--size-value4);

    svg.vc-notif {
      font-size: var(--size-value4);
      height: var(--size-value4);
      min-width: var(--size-value4);
    }

    svg.arrow-right {
      fill: var(--blue);
    }
  }

  .alert-header {
    font-weight: bold;
    font-size: var(--font-size-x-small);
    line-height: var(--size-value5);
    letter-spacing: var(--letter-spacing);
    margin-bottom: var(--size-value1);
    color: var(--gray-darken-5);

    &:after {
      content: ' ';
      padding-right: var(--size-value1);
    }
  }

  .alert-body {
    font-size: var(--font-size-x-small);
    line-height: var(--size-value5);
    letter-spacing: -0.01em;
    color: var(--gray-darken-5);

    a {
      color: var(--link-color);
    }

    &--with-inline-action {
      margin-inline-end: var(--size-value1);
    }
  }

  .inline-action {
    font-size: var(--font-size-x-small);
    line-height: var(--size-value5);
    font-weight: var(--font-weight-large2);
    cursor: pointer;
  }

  .VcButton {
    .v-icon {
      margin-right: initial;
      align-items: initial;
    }

    &.action-button {
      height: var(--size-value6);
      color: var(--blue) !important;
      font-weight: var(--font-weight-large);
      font-size: var(--font-size-x-small);
      line-height: var(--size-value6);
      margin-top: var(--size-value1);
      padding: var(--size-value0);

      span.v-btn__content {
        opacity: unset !important;

        .v-icon:before {
          content: ' ';
          padding-right: var(--size-value1);
        }
      }
    }

    &.multi-action-button {
      margin-top: var(--size-value3);
      margin-inline-end: var(--size-value2);
    }
  }
}
</style>
